import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getJson, postJson } from '@/api/client'
import type { ShadowConversationsPage, ShadowImportResult, ShadowRunResult, ShadowStats } from '@/api/types'
import { Badge } from '@/ui/primitives'
import { Button } from '@/ui/Button'
import { toast } from '@/ui/Toast'

const PAGE_SIZE = 25
const CATEGORIES = [null, 'beauty', 'order_status', 'complaint', 'pricing', 'general'] as const

const CAT_COLORS: Record<string, string> = {
  beauty: '#6A8D6A',
  order_status: '#C4A35A',
  complaint: '#C4705A',
  pricing: '#7A6A8A',
  product_availability: '#5A8A8A',
  store_info: '#8A7A5A',
  how_to_use: '#5A6A8A',
  social_media: '#8A5A6A',
  general: '#A09888',
}

function catLabel(cat: string | null): string {
  if (!cat) return '?'
  const m: Record<string, string> = {
    beauty: 'Beauty', order_status: 'Order', complaint: 'Complaint',
    pricing: 'Pricing', product_availability: 'Avail.', store_info: 'Store',
    how_to_use: 'How-to', social_media: 'Social', general: 'General',
  }
  return m[cat] || cat
}

function catBadge(cat: string | null): 'default' | 'success' | 'warning' | 'danger' {
  if (cat === 'complaint') return 'danger'
  if (cat === 'order_status') return 'warning'
  if (cat === 'beauty') return 'success'
  return 'default'
}

function formatSpan(first: string | null, last: string | null): string {
  if (!first) return ''
  const f = new Date(first)
  if (!last) return f.toLocaleDateString()
  const l = new Date(last)
  if (f.toDateString() === l.toDateString()) {
    return `${f.toLocaleDateString()} ${f.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${l.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  }
  return `${f.toLocaleDateString()} – ${l.toLocaleDateString()}`
}

function formatLatency(ms: number | null | undefined): string {
  if (ms == null || ms === 0) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60000).toFixed(1)}m`
}

export function ShadowDashboard() {
  const navigate = useNavigate()
  const [page, setPage] = useState<ShadowConversationsPage | null>(null)
  const [stats, setStats] = useState<ShadowStats | null>(null)
  const [filterStatus, setFilterStatus] = useState<string | null>(null)
  const [filterCategory, setFilterCategory] = useState<string | null>(null)
  const [filterSelected, setFilterSelected] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [importing, setImporting] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void loadAll() }, [filterStatus, filterCategory, filterSelected, currentPage])

  async function loadAll() {
    try {
      const params = new URLSearchParams()
      params.set('offset', String(currentPage * PAGE_SIZE))
      params.set('limit', String(PAGE_SIZE))
      if (filterStatus) params.set('status', filterStatus)
      if (filterCategory) params.set('category', filterCategory)
      if (filterSelected) params.set('selected', 'true')

      const [pageData, statsRow] = await Promise.all([
        getJson<ShadowConversationsPage>(`/v1/admin/shadow/conversations?${params}`),
        getJson<ShadowStats>('/v1/admin/shadow/stats'),
      ])
      setPage(pageData)
      setStats(statsRow)
      setError(null)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  async function handleImport() {
    setImporting(true)
    setError(null)
    try {
      const result = await postJson<ShadowImportResult>('/v1/admin/shadow/import', {})
      toast.success(`Imported ${result.new_conversations} conversations (${result.new_turns} turns)`)
      setCurrentPage(0)
      await loadAll()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setImporting(false) }
  }

  async function handleRun() {
    setRunning(true)
    setError(null)
    try {
      const result = await postJson<ShadowRunResult>('/v1/admin/shadow/run', {
        limit: null,
        conversation_ids: null,
      })
      toast.success(`Ran ${result.total_turns_ran} turns, cost $${result.total_cost_usd.toFixed(4)}`)
      await loadAll()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally { setRunning(false) }
  }

  const totalPages = page ? Math.ceil(page.total / PAGE_SIZE) : 0
  const conversations = page?.conversations || []
  const catBreakdown = stats?.category_breakdown || {}
  const hasAnalytics = Object.keys(catBreakdown).length > 0
  const maxCatCount = hasAnalytics ? Math.max(...Object.values(catBreakdown)) : 1

  const badgeVariant = (status: string): 'default' | 'success' | 'warning' | 'danger' => {
    if (status === 'done') return 'success'
    if (status === 'running') return 'warning'
    if (status === 'failed') return 'danger'
    return 'default'
  }

  return (
    <div className="px-8 lg:px-14 py-12 h-full overflow-y-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 font-sans text-xs text-mute mb-3">
          <span className="w-1.5 h-1.5 bg-leaf" />
          <span>Admin / Shadow</span>
        </div>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-4xl font-medium text-cream tracking-tight leading-[1.05]">
              Shadow Testing
            </h1>
            {stats && (
              <p className="text-sm text-mute mt-2">
                {stats.total_conversations} conversations · {stats.total_turns} turns · {stats.selected} selected
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" loading={importing} onClick={handleImport}>Import</Button>
            <Button variant="primary" size="sm" loading={running} onClick={handleRun}>Re-run All</Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 font-display italic text-sm border-l-[3px] border-ember text-ember pl-4 py-2 bg-ember/5">
          {error}
        </div>
      )}

      {/* KPI Bar */}
      {stats && (
        <div className="grid grid-cols-4 gap-3 mb-8">
          <div className="border border-haze/30 bg-ink-raised p-4">
            <div className="text-[10px] text-mute uppercase tracking-wider mb-1">Conversations</div>
            <div className="font-display text-2xl text-cream">{stats.total_conversations}</div>
          </div>
          <div className="border border-haze/30 bg-ink-raised p-4">
            <div className="text-[10px] text-mute uppercase tracking-wider mb-1">Turns</div>
            <div className="font-display text-2xl text-cream">{stats.total_turns}</div>
          </div>
          <div className="border border-haze/30 bg-ink-raised p-4">
            <div className="text-[10px] text-mute uppercase tracking-wider mb-1">Total Cost</div>
            <div className="font-display text-2xl text-cream">${stats.total_cost_usd.toFixed(4)}</div>
          </div>
          <div className="border border-haze/30 bg-ink-raised p-4">
            <div className="text-[10px] text-mute uppercase tracking-wider mb-1">Avg Latency</div>
            <div className="font-display text-2xl text-cream">{formatLatency(stats.avg_latency_ms)}</div>
          </div>
        </div>
      )}

      {/* Analytics Section */}
      {hasAnalytics && stats && (
        <div className="grid grid-cols-2 gap-8 mb-10">
          {/* Category Stacked Bar */}
          <div className="border border-haze/30 bg-ink-raised p-6">
            <div className="text-xs text-mute uppercase tracking-wider mb-4">Category Distribution</div>
            <div className="flex flex-col-reverse h-56 mb-3" style={{ gap: 0 }}>
              {Object.entries(catBreakdown)
                .sort(([, a], [, b]) => b - a)
                .map(([cat, count]) => {
                  const pct = (count / stats.total_conversations) * 100
                  const color = CAT_COLORS[cat] || '#A09888'
                  if (pct < 0.5) return null
                  return (
                    <div
                      key={cat}
                      className="flex items-center px-2 text-[10px] text-cream transition-all duration-300 min-h-[14px]"
                      style={{
                        height: `${Math.max(pct * 0.56, 1)}rem`,
                        backgroundColor: color,
                        opacity: 0.85,
                        minWidth: 0,
                      }}
                      title={`${catLabel(cat)}: ${count}`}
                    >
                      <span className="truncate">{catLabel(cat)}</span>
                      <span className="ml-auto flex-shrink-0">{count}</span>
                    </div>
                  )
                })}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
              {Object.entries(catBreakdown)
                .sort(([, a], [, b]) => b - a)
                .map(([cat, count]) => (
                  <div key={cat} className="flex items-center gap-1.5 text-[10px]">
                    <span className="w-2 h-2 flex-shrink-0" style={{ backgroundColor: CAT_COLORS[cat] || '#A09888' }} />
                    <span className="text-mute">{catLabel(cat)}</span>
                    <span className="text-cream/70">{count}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Category Leaderboard */}
          <div className="border border-haze/30 bg-ink-raised p-6">
            <div className="text-xs text-mute uppercase tracking-wider mb-4">Category Breakdown</div>
            <div className="space-y-2">
              {Object.entries(catBreakdown)
                .sort(([, a], [, b]) => b - a)
                .map(([cat, count]) => (
                  <div key={cat} className="flex items-center gap-3">
                    <span className="text-xs text-cream w-16 flex-shrink-0 text-right">{catLabel(cat)}</span>
                    <div className="flex-1 h-5 bg-ink overflow-hidden relative">
                      <div
                        className="absolute inset-y-0 left-0 transition-all duration-500"
                        style={{
                          width: `${(count / maxCatCount) * 100}%`,
                          backgroundColor: CAT_COLORS[cat] || '#A09888',
                          opacity: 0.6,
                        }}
                      />
                    </div>
                    <span className="text-xs text-cream/60 w-8 flex-shrink-0">{count}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Conversations */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-xs text-mute uppercase tracking-wider">Conversations</span>
          <div className="flex gap-1.5 flex-wrap">
            {[null, 'pending', 'running', 'done', 'failed'].map((s) => (
              <button
                key={s ?? 'all'}
                onClick={() => { setFilterStatus(s); setCurrentPage(0) }}
                className={`px-2.5 py-0.5 text-[10px] transition-colors ${
                  filterStatus === s ? 'bg-ember text-ink' : 'bg-ink-raised text-mute hover:text-cream'
                }`}
              >{s ?? 'all'}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        {CATEGORIES.map((c) => (
          <button
            key={c ?? 'all'}
            onClick={() => { setFilterCategory(c); setCurrentPage(0) }}
            className={`px-2.5 py-0.5 text-[10px] transition-colors ${
              filterCategory === c ? 'bg-ember text-ink' : 'bg-ink-raised text-mute hover:text-cream'
            }`}
          >{c ? catLabel(c) : 'all categories'}</button>
        ))}
        <div className="w-px h-4 bg-haze/30 mx-0.5" />
        <button
          onClick={() => { setFilterSelected(!filterSelected); setCurrentPage(0) }}
          className={`px-2.5 py-0.5 text-[10px] transition-colors ${
            filterSelected ? 'bg-leaf/20 text-leaf border border-leaf/30' : 'bg-ink-raised text-mute hover:text-cream'
          }`}
        >{filterSelected ? '★ Selected' : 'All'}</button>
      </div>

      <div className="border border-haze/30 bg-ink-raised overflow-hidden">
        <div className="divide-y divide-haze/20">
          {conversations.length === 0 ? (
            <div className="px-5 py-12 text-center text-mute text-xs">
              No conversations yet. Click Import to load data from the remote export.
            </div>
          ) : (
            conversations.map((conv) => (
              <div key={conv.id}>
                <button
                  onClick={() => navigate(`/admin/shadow/${conv.id}`)}
                  className="w-full px-4 py-2.5 text-left transition-all duration-200 hover:bg-ink/30"
                >
                  <div className="grid grid-cols-[1fr_100px_56px_80px_80px] gap-3 items-center">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="text-sm text-cream font-display truncate">
                        {conv.customer_name || `User #${conv.remote_from_id}`}
                      </span>
                      <Badge variant={badgeVariant(conv.status)}>{conv.status}</Badge>
                      {conv.primary_category && (
                        <Badge variant={catBadge(conv.primary_category)}>{catLabel(conv.primary_category)}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-mute text-center">
                      {conv.total_human_messages} turns
                    </div>
                    <div className="text-xs text-mute text-center">
                      {conv.total_ai_turns > 0 ? (
                        <span>{conv.total_ai_turns} AI</span>
                      ) : (
                        <span className="text-mute/30">—</span>
                      )}
                    </div>
                    <div className="text-xs text-mute text-right">
                      ${conv.total_cost_usd.toFixed(4)}
                    </div>
                    <div className="text-[10px] text-mute/50 text-right">
                      {conv.first_message_ts ? formatSpan(conv.first_message_ts, conv.last_message_ts) : ''}
                    </div>
                  </div>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-5">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="px-2.5 py-1 text-[10px] border border-haze/40 text-mute hover:text-cream hover:border-haze/60 disabled:opacity-30 transition-colors"
          >← Prev</button>
          {(() => {
            const pages: (number | '...')[] = []
            const add = (p: number) => { if (!pages.includes(p)) pages.push(p) }
            if (totalPages <= 7) {
              for (let i = 0; i < totalPages; i++) add(i)
            } else {
              add(0)
              const start = Math.max(1, currentPage - 1)
              const end = Math.min(totalPages - 2, currentPage + 1)
              if (start > 1) pages.push('...')
              for (let i = start; i <= end; i++) add(i)
              if (end < totalPages - 2) pages.push('...')
              add(totalPages - 1)
            }
            return pages.map((p, i) =>
              p === '...' ? (
                <span key={`e${i}`} className="text-[10px] text-mute px-1">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`px-2.5 py-1 text-[10px] transition-colors ${
                    currentPage === p
                      ? 'bg-ember text-ink'
                      : 'border border-haze/40 text-mute hover:text-cream hover:border-haze/60'
                  }`}
                >{p + 1}</button>
              )
            )
          })()}
          <button
            onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
            disabled={currentPage >= totalPages - 1}
            className="px-2.5 py-1 text-[10px] border border-haze/40 text-mute hover:text-cream hover:border-haze/60 disabled:opacity-30 transition-colors"
          >Next →</button>
        </div>
      )}

      {page && (
        <div className="text-center text-[10px] text-mute mt-3">
          {page.offset + 1}–{Math.min(page.offset + page.limit, page.total)} of {page.total}
        </div>
      )}
    </div>
  )
}
